import { supabase } from '@/lib/supabaseClient'

import {
  IMAGE_SURFACES,
  PUBLIC_MEDIA_BUCKET,
  SUPABASE_STORAGE_PROVIDER,
  type ImageAssetInput,
  type ImageSurface,
} from './imageAssetFields'
import {
  compressImageForSurface,
  getFileExtension,
  readImageDimensions,
} from './imageCompression'

function ensureSurface(surface: string): asserts surface is ImageSurface {
  if (!Object.values(IMAGE_SURFACES).includes(surface as ImageSurface)) {
    throw new Error(`Unsupported image surface: ${surface}`)
  }
}

export function getSupabasePublicUrl(bucket: string, path: string): string {
  if (!bucket || !path) return ''
  const result = supabase.storage.from(bucket).getPublicUrl(path)
  return result?.data?.publicUrl || ''
}

interface UploadPublicImageOptions {
  file: File
  surface: string
  userId: string
  bucket?: string
}

interface DuplicatePublicImageOptions {
  asset: ImageAssetInput
  surface: string
  userId: string
}

export async function uploadPublicImage({
  file,
  surface,
  userId,
  bucket = PUBLIC_MEDIA_BUCKET,
}: UploadPublicImageOptions): Promise<ImageAssetInput> {
  ensureSurface(surface)

  if (!userId) {
    throw new Error('A user id is required to upload images.')
  }

  const compressedFile = await compressImageForSurface(file, surface)
  const dimensions = await readImageDimensions(compressedFile)
  const extension = getFileExtension(compressedFile)
  const objectPath = `${surface}/${userId}/${crypto.randomUUID()}.${extension}`

  const { error } = await supabase.storage
    .from(bucket)
    .upload(objectPath, compressedFile, {
      cacheControl: '3600',
      upsert: false,
      contentType: compressedFile.type || file.type,
    })

  if (error) throw error

  return {
    provider: SUPABASE_STORAGE_PROVIDER,
    bucket,
    path: objectPath,
    public_url: getSupabasePublicUrl(bucket, objectPath),
    mime_type: compressedFile.type || file.type || '',
    bytes: compressedFile.size || file.size || null,
    width: dimensions.width,
    height: dimensions.height,
    original_name: file.name || '',
  }
}

export async function duplicatePublicImage({
  asset,
  surface,
  userId,
}: DuplicatePublicImageOptions): Promise<ImageAssetInput> {
  const sourceUrl = asset.public_url || getSupabasePublicUrl(asset.bucket, asset.path)
  if (!sourceUrl) throw new Error('The current portrait could not be located.')

  const response = await fetch(sourceUrl)
  if (!response.ok) throw new Error('The current portrait could not be copied.')
  const blob = await response.blob()
  const file = new File(
    [blob],
    asset.original_name || 'portrait',
    { type: blob.type || asset.mime_type || 'application/octet-stream' },
  )

  return uploadPublicImage({ file, surface, userId })
}

export async function deletePublicImage(asset: { bucket?: string; path?: string } | null | undefined): Promise<void> {
  if (!asset?.bucket || !asset?.path) return

  const { error } = await supabase.storage.from(asset.bucket).remove([asset.path])
  if (error) throw error
}
